import type { Permission, SessionView } from "@dentos/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, type ReactNode } from "react";
import { ApiError, api, setUnauthenticatedHandler } from "./api";

interface SessionState {
  session: SessionView | null;
  loading: boolean;
  /** Shapes the UI only. The API checks every permission again. */
  can: (p: Permission) => boolean;
  refresh: () => Promise<void>;
  setSession: (s: SessionView | null) => void;
}

const Ctx = createContext<SessionState | null>(null);
export const SESSION_KEY = ["session"];

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: SESSION_KEY,
    queryFn: async () => {
      try {
        return await api.get<SessionView>("/api/auth/session");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      qc.setQueryData(SESSION_KEY, null);
      qc.removeQueries({ predicate: (query) => query.queryKey[0] !== "session" });
    });
  }, [qc]);

  const session = q.data ?? null;
  const value: SessionState = {
    session,
    loading: q.isPending,
    can: (p) => !!session?.permissions.includes(p),
    refresh: async () => {
      await qc.invalidateQueries({ queryKey: SESSION_KEY });
    },
    setSession: (s) => {
      // A different user or clinic must never see the previous one's cached data.
      qc.removeQueries({ predicate: (query) => query.queryKey[0] !== "session" });
      qc.setQueryData(SESSION_KEY, s);
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSession outside SessionProvider");
  return ctx;
}
